import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { Receipt, AlertTriangle, CheckCircle2, FileText, ChevronRight } from 'lucide-react';
import { Modal, Pill, btn } from '../common/AdminUI';
import { listPurDocuments, getPurDocument, money } from './reconApi';

/**
 * Every purchase bill read against one creditor, invoice by invoice.
 *
 * No two brands lay their bill out the same way, so the line table is not
 * written against any one of them: the columns every brand shares are fixed,
 * and the rest are discovered from each document's own rows — which is exactly
 * how the server stores them, common fields as columns and the remainder kept
 * verbatim in `data`.
 */

/* ── naming ──────────────────────────────────────────────────────────────── */

// The things a freight bill says in capitals, so a derived heading does not
// come out as "Awb charges" or "Igst amt".
const SHOUT = /^(awb|mawb|ams|fsc|hsn|sac|gst|igst|cgst|sgst|tds|pkgs?|no|id)$/i;

/** `airwaysbillCharges` → `Airwaysbill charges`, `amsCharges` → `AMS charges`. */
const humanise = (key) => {
  const words = String(key).replace(/([a-z0-9])([A-Z])/g, '$1 $2').split(/[\s_]+/);
  return words
    .map((w, i) => (SHOUT.test(w) ? w.toUpperCase() : i === 0 ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : w.toLowerCase()))
    .join(' ');
};

const isNum = (v) => v !== null && v !== '' && v !== undefined && !Number.isNaN(Number(v));

const show = (v) => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

/* ── the line table, built from whatever the rows carry ──────────────────── */

// Shared by every brand, so these keep a fixed place and order.
const FIXED = [
  { key: 'lineNo', label: '#', pick: (l) => l.lineNo },
  { key: 'awbNumber', label: 'AWB', pick: (l) => l.awbNumber, mono: true },
  { key: 'hsnSac', label: 'HSN/SAC', pick: (l) => l.hsnSac, mono: true },
];

const TAXES = [
  ['cgstRate', 'CGST %'], ['cgstAmt', 'CGST'],
  ['sgstRate', 'SGST %'], ['sgstAmt', 'SGST'],
  ['igstRate', 'IGST %'], ['igstAmt', 'IGST'],
];

const LineTable = ({ lines }) => {
  const columns = useMemo(() => {
    const cols = FIXED.filter((c) => lines.some((l) => c.pick(l) !== null && c.pick(l) !== undefined));

    // Brand-specific fields, in the order the first row that has them lists
    // them — a bill reads better in its own order than in alphabetical order.
    const seen = [];
    lines.forEach((l) => Object.keys(l.data || {}).forEach((k) => { if (!seen.includes(k)) seen.push(k); }));
    seen.forEach((k) => cols.push({
      key: `data.${k}`, label: humanise(k), pick: (l) => (l.data || {})[k],
    }));

    // Taxes last, and only the ones this brand actually charges.
    TAXES.forEach(([k, label]) => {
      if (lines.some((l) => l[k] !== null && l[k] !== undefined)) {
        cols.push({ key: k, label, pick: (l) => l[k] });
      }
    });

    // A column of numbers lines up on the right; one of words does not.
    return cols.map((c) => {
      const vals = lines.map(c.pick).filter((v) => v !== null && v !== undefined && v !== '');
      return { ...c, numeric: vals.length > 0 && vals.every(isNum) };
    });
  }, [lines]);

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
      <table className="min-w-full">
        <thead>
          <tr className="border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/50">
            {columns.map((c) => (
              <th key={c.key}
                className={`px-3 py-2.5 text-[9.5px] font-black uppercase tracking-[0.1em] whitespace-nowrap
                            text-slate-400 dark:text-slate-500 ${c.numeric ? 'text-right' : 'text-left'}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
          {lines.map((l) => (
            <tr key={l.lineNo} className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition">
              {columns.map((c) => (
                <td key={c.key}
                  className={`px-3 py-2.5 text-[12.5px] whitespace-nowrap text-slate-700 dark:text-slate-300
                              ${c.numeric ? 'text-right tabular-nums' : ''} ${c.mono ? 'font-mono' : ''}`}>
                  {show(c.pick(l))}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

/* ── the modal ───────────────────────────────────────────────────────────── */

/* Two different things, and they used to share a name: how well the bill was
   read, and how much of it has been paid. */
const ExtractionPill = ({ status }) => (
  status === 'needs_review'
    ? <Pill tone="amber">Needs review</Pill>
    : <Pill tone="emerald">Read</Pill>
);

export const PAYMENT_LABEL = { paid: 'Paid', partially_paid: 'Partially Paid', unpaid: 'Unpaid' };
export const PAYMENT_TONE = { paid: 'emerald', partially_paid: 'amber', unpaid: 'rose' };

const PaymentPill = ({ status }) => (
  <Pill tone={PAYMENT_TONE[status] || 'rose'} dot>{PAYMENT_LABEL[status] || 'Unpaid'}</Pill>
);

/**
 * Two ways in: a creditor, which lists every bill read from them, or one
 * document, opened on its own from the shipment it was filed against.
 */
const PurViewerModal = ({ open, onClose, creditor, documentId }) => {
  const single = Boolean(documentId);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [active, setActive] = useState(null);
  const [loadingOne, setLoadingOne] = useState(false);

  useEffect(() => {
    if (!open || single) return;
    if (!creditor) return;
    let alive = true;
    setLoading(true);
    setActiveId(null);
    setActive(null);
    listPurDocuments(creditor.id)
      .then((list) => {
        if (!alive) return;
        setDocs(list);
        // Open the newest on arrival; a list of invoice numbers alone tells
        // nobody anything.
        if (list.length) setActiveId(list[0].id);
      })
      .catch(() => { if (alive) toast.error('Could not load this creditor’s bills'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [open, creditor]);

  // Opened on one bill, there is no list to pick from.
  useEffect(() => {
    if (open && single) { setDocs([]); setActiveId(documentId); }
  }, [open, single, documentId]);

  useEffect(() => {
    if (!activeId) return;
    let alive = true;
    setLoadingOne(true);
    getPurDocument(activeId)
      .then((d) => { if (alive) setActive(d); })
      .catch(() => { if (alive) toast.error('Could not open that bill'); })
      .finally(() => { if (alive) setLoadingOne(false); });
    return () => { alive = false; };
  }, [activeId]);

  const totals = active?.totals || {};

  return (
    <Modal
      open={open}
      onClose={onClose}
      icon={Receipt}
      tone="violet"
      title={single ? 'Purchase bill' : 'Purchase bills'}
      subtitle={single ? (active?.creditor?.name || active?.brand) : creditor?.name}
      width={single ? 'max-w-4xl' : 'max-w-6xl'}
      footer={(
        <div className="flex justify-end">
          <button type="button" className={btn.quiet} onClick={onClose}>Close</button>
        </div>
      )}
    >
      {loading ? (
        <p className="py-12 text-center text-sm text-slate-400">Loading bills…</p>
      ) : !single && docs.length === 0 ? (
        <div className="py-12 text-center">
          <FileText size={30} className="mx-auto text-slate-300 dark:text-slate-600" />
          <p className="mt-3 text-sm font-semibold text-slate-600 dark:text-slate-300">
            Nothing read from {creditor?.name} yet
          </p>
          <p className="mt-1 text-[12.5px] text-slate-400 max-w-sm mx-auto">
            A bill is read when its PDF is uploaded on the shipment it covers.
          </p>
        </div>
      ) : (
        <div className={single ? '' : 'grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-5'}>

          {/* invoice by invoice */}
          <div className={`min-w-0 ${single ? 'hidden' : ''}`}>
            <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-2">
              {docs.length} invoice{docs.length === 1 ? '' : 's'}
            </p>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              {docs.map((d, i) => {
                const on = d.id === activeId;
                return (
                  <button key={d.id} type="button" onClick={() => setActiveId(d.id)}
                    className={`w-full text-left px-3.5 py-3 transition flex items-start gap-2
                                ${i > 0 ? 'border-t border-slate-100 dark:border-slate-800' : ''}
                                ${on ? 'bg-violet-50 dark:bg-violet-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/50'}`}>
                    <ChevronRight size={14}
                      className={`mt-0.5 flex-shrink-0 transition ${on ? 'text-violet-500 translate-x-0.5' : 'text-slate-300 dark:text-slate-600'}`} />
                    <span className="min-w-0 flex-1">
                      <span className={`block font-mono text-[12.5px] font-bold truncate
                                        ${on ? 'text-violet-700 dark:text-violet-300' : 'text-slate-900 dark:text-white'}`}>
                        {d.invoiceNumber || '(no number)'}
                      </span>
                      <span className="block text-[11px] text-slate-400 mt-0.5 tabular-nums">
                        {d.invoiceDate || '—'} · {d.lineCount} line{d.lineCount === 1 ? '' : 's'}
                      </span>
                      {d.extractionStatus === 'needs_review' && (
                        <span className="inline-flex items-center gap-1 mt-1.5 text-[10.5px] font-bold text-amber-600 dark:text-amber-400">
                          <AlertTriangle size={11} /> Needs review
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* the bill itself */}
          <div className="min-w-0">
            {loadingOne || !active ? (
              <p className="py-12 text-center text-sm text-slate-400">Opening…</p>
            ) : (
              <>
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2.5">
                      <h4 className="font-mono text-[15px] font-bold text-slate-900 dark:text-white">
                        {active.invoiceNumber || '(no number)'}
                      </h4>
                      <ExtractionPill status={active.extractionStatus} />
                      <PaymentPill status={active.status} />
                    </div>
                    <p className="text-[12px] text-slate-500 dark:text-slate-400 mt-1">
                      {active.brand} · {active.invoiceDate || 'no date'}
                    </p>
                  </div>
                </div>

                {active.warnings?.length > 0 && (
                  <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3
                                  dark:border-amber-500/30 dark:bg-amber-500/[0.07]">
                    <p className="text-[12.5px] font-bold text-amber-700 dark:text-amber-300 flex items-center gap-2">
                      <AlertTriangle size={14} /> What the reader was unsure about
                    </p>
                    <ul className="mt-1.5 space-y-1">
                      {active.warnings.map((w, i) => (
                        <li key={i} className="text-[12px] text-slate-600 dark:text-slate-300">· {w}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <LineTable lines={active.lineItems || []} />

                {Object.keys(totals).length > 0 && (
                  <div className="mt-3 rounded-xl border border-slate-200 dark:border-slate-800 px-4 py-3">
                    <p className="text-[9.5px] font-black uppercase tracking-[0.12em] text-slate-400 dark:text-slate-500 mb-2.5">
                      Totals on the bill
                    </p>
                    <div className="flex flex-wrap gap-x-8 gap-y-3">
                      {Object.entries(totals).map(([k, v]) => (
                        <div key={k}>
                          <p className="text-[10.5px] text-slate-400">{humanise(k)}</p>
                          <p className="text-[14px] font-bold text-slate-900 dark:text-white tabular-nums mt-0.5">
                            {isNum(v) ? `₹${money(v)}` : show(v)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {active.extractionStatus !== 'needs_review' && (
                  <p className="mt-2.5 text-[11.5px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5">
                    <CheckCircle2 size={13} /> Read without warnings
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default PurViewerModal;
